import React, { useState, useRef, useEffect } from 'react';
import { extractPasswordProtectedArchive } from '../services/archiveService';
import { isCryptoSupported } from '../services/cryptoService';
import * as zipjs from '@zip.js/zip.js';

/**
 * Компонент для разархивации локальных файлов с паролем
 */
const LocalFileExtractor = () => {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [extractedFiles, setExtractedFiles] = useState([]);
  const [fsApiSupported, setFsApiSupported] = useState(false);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Проверяем поддержку WebCrypto API
  const cryptoSupported = isCryptoSupported();

  // Проверяем поддержку File System Access API
  useEffect(() => {
    const checkFsApiSupport = () => {
      return (
        'showDirectoryPicker' in window &&
        'showOpenFilePicker' in window &&
        'showSaveFilePicker' in window
      );
    };

    setFsApiSupported(checkFsApiSupport());
  }, []);

  /**
   * Обработчик выбора файла
   */
  const handleFileChange = event => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Проверяем, что это ZIP-файл
      if (
        selectedFile.type === 'application/zip' ||
        selectedFile.name.toLowerCase().endsWith('.zip')
      ) {
        setFile(selectedFile);
        setError(null);
      } else {
        setFile(null);
        setError('Please choose a ZIP file');
      }
    }
  };

  /**
   * Обработчик изменения пароля
   */
  const handlePasswordChange = event => {
    setPassword(event.target.value);
    setError(null);
  };

  /**
   * Обработчики drag & drop
   */
  const onDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropRef.current) {
      setIsDragging(false);
    }
  };

  const onDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      // Проверяем, что это ZIP-файл
      if (
        droppedFile.type === 'application/zip' ||
        droppedFile.name.toLowerCase().endsWith('.zip')
      ) {
        setFile(droppedFile);
        setError(null);
      } else {
        setFile(null);
        setError('Please drag a ZIP file');
      }
    }
  };

  /**
   * Валидация формы перед отправкой
   */
  const validateForm = () => {
    if (!file) {
      setError('Please select a ZIP file to extract');
      return false;
    }

    if (!password) {
      setError('Пожалуйста, введите пароль для разархивации');
      return false;
    }

    return true;
  };

  /**
   * Обработчик отправки формы
   */
  const handleSubmit = async event => {
    event.preventDefault();

    if (!cryptoSupported) {
      setError(
        'Ваш браузер не поддерживает Web Crypto API. Пожалуйста, используйте современный браузер.'
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      setError(null);
      setSuccess(false);
      setExtractedFiles([]);
  

      // Extract with password
      setProgress(25);
      const files = await extractPasswordProtectedArchive(file, password);
      setProgress(75);

      if (!files || files.length === 0) {
        throw new Error('Failed to extract files. Password may be incorrect.');
      }

      setExtractedFiles(files);
      setProgress(100);
      setSuccess(true);
    } catch (error) {
      
      let errorMessage = 'Произошла ошибка при разархивации файла';

      if (error.message && error.message.includes('password')) {
        errorMessage = 'Неверный пароль для архива';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Неизвестная ошибка';
        }
      } else if (error) {
        errorMessage = String(error);
      }

      setError(`Ошибка при разархивации файла: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Скачивание извлеченного файла
   */
  const downloadFile = file => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Скачивание всех извлеченных файлов по отдельности
   */
  const downloadAllFilesIndividually = () => {
    extractedFiles.forEach(file => {
      downloadFile(file);
    });
  };

  /**
   * Разархивация файлов непосредственно в каталог на компьютере пользователя
   * с использованием File System Access API
   */
  const extractToDirectory = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setProgress(0);

      // Запрашиваем у пользователя выбор каталога для сохранения файлов
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads',
        id: 'extractZipToDirectory',
      });

      setProgress(10);

      // Группируем файлы по директориям
      const filesByPath = {};

      // Анализируем пути файлов
      extractedFiles.forEach(file => {
        const fullPath = file.name;
        filesByPath[fullPath] = file;
      });

      // Создаем структуру каталогов и записываем файлы
      const entries = Object.entries(filesByPath);

      for (let i = 0; i < entries.length; i++) {
        const [path, file] = entries[i];

        // Разбиваем путь на компоненты
        const pathParts = path.split('/');
        const fileName = pathParts.pop(); // Последний элемент - имя файла

        // Создаем вложенные каталоги, если они есть
        let currentDirHandle = dirHandle;

        for (const dirName of pathParts) {
          if (dirName) {
            // Пропускаем пустые имена каталогов
            try {
              // Пытаемся получить существующий каталог
              currentDirHandle = await currentDirHandle.getDirectoryHandle(dirName, {
                create: true,
              });
            } catch (err) {
              throw new Error(`Failed to create directory ${dirName}: ${err.message}`);
            }
          }
        }

        // Создаем файл в текущем каталоге
        const fileHandle = await currentDirHandle.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();

        // Читаем содержимое файла
        const fileData = await file.arrayBuffer();

        // Записываем данные в файл
        await writable.write(fileData);
        await writable.close();

        // Обновляем прогресс
        const progress = Math.floor(10 + (i / entries.length) * 90);
        setProgress(progress);
      }

      setProgress(100);
      setSuccess(true);
      setError(null);
    } catch (error) {
      

      // Обработка специфических ошибок
      if (error.name === 'AbortError') {
        setError('Операция была отменена пользователем');
      } else if (error.name === 'NotAllowedError') {
        setError('Доступ к каталогу не был предоставлен');
      } else {
        setError(`Ошибка при разархивации в каталог: ${error.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Создание и скачивание ZIP-архива со всеми извлеченными файлами
   * с сохранением структуры каталогов
   */
  const downloadAllFilesAsZip = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setProgress(0);
      setSuccess(false);

      // Создаем новый ZIP-архив (zip.js)
      const blobWriter = new zipjs.BlobWriter('application/zip');
      const zipWriter = new zipjs.ZipWriter(blobWriter, { level: 6, useWebWorkers: true });

      const total = extractedFiles.length;
      for (let i = 0; i < total; i++) {
        const file = extractedFiles[i];
        const reader = new zipjs.BlobReader(file);
        const name = file.name; // структура каталогов в исходном архиве может быть потеряна
        await zipWriter.add(name, reader, {
          lastModDate: new Date(file.lastModified || Date.now()),
          level: 6,
        });
        const progress = Math.floor(10 + (i / total) * 60);
        setProgress(progress);
      }

      setProgress(70);
      const blob = await zipWriter.close();

      // Создаем имя для архива
      const zipFileName = file
        ? `${file.name.replace('.zip', '')}_extracted.zip`
        : 'extracted_files.zip';

      // Скачиваем архив
      setProgress(90);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setProgress(100);
      setSuccess(true);

      // Показываем сообщение об успешном скачивании
      setError(null);
    } catch (error) {
      
      setError(`Failed to create ZIP: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Отображение списка извлеченных файлов
   */
  const renderExtractedFiles = () => {
    if (!extractedFiles.length) {
      return null;
    }

    return (
      <div className="extracted-files">
        <h3>Извлеченные файлы ({extractedFiles.length}):</h3>
        <div className="download-options">
          <h4>Choose how to download:</h4>
          <div className="file-actions">
            {fsApiSupported && (
              <button
                type="button"
                className="action-button extract-to-directory-button primary-action"
                onClick={extractToDirectory}
                disabled={isProcessing}
              >
                <span className="button-icon">📂</span>
                <span className="button-text">Extract to directory</span>
                <span className="button-description">Saves files to disk</span>
              </button>
            )}
            <button
              type="button"
              className={`action-button download-zip-button ${!fsApiSupported ? 'primary-action' : ''}`}
              onClick={downloadAllFilesAsZip}
              disabled={isProcessing}
            >
              <span className="button-icon">📦</span>
              <span className="button-text">Download as ZIP</span>
              <span className="button-description">Creates a new archive</span>
            </button>
            <button
              type="button"
              className="action-button download-all-button"
              onClick={downloadAllFilesIndividually}
              disabled={isProcessing}
            >
              <span className="button-icon">📥</span>
              <span className="button-text">Download files individually</span>
            </button>
          </div>
        </div>
        <ul className="file-list">
          {extractedFiles.map((file, index) => (
            <li key={index} className="file-item">
              <span className="file-name">📄 {file.name}</span>
              <span className="file-size">({formatFileSize(file.size)})</span>
              <button
                type="button"
                className="download-file-button"
                onClick={() => downloadFile(file)}
              >
                📥
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  /**
   * Форматирование размера файла
   */
  const formatFileSize = bytes => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Отображение индикатора прогресса
   */
  const renderProgressBar = () => {
    if (!isProcessing) {
      return null;
    }

    return (
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        <div className="progress-text">{progress}%</div>
      </div>
    );
  };

  if (!cryptoSupported) {
    return (
      <div className="error-container">
        <h3>Compatibility issue</h3>
        <p>Your browser does not support Web Crypto API required to extract files.</p>
        <p>Please use a modern browser like Chrome, Firefox, Safari or Edge.</p>
      </div>
    );
  }

  return (
    <div className="local-file-extractor">
      <h2>Extract Password‑Protected ZIP</h2>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="zip-file-input">Choose a ZIP file to extract:</label>
          <div className="file-input-container">
            {/* Скрытый input для выбора файла */}
            <input
              type="file"
              id="zip-file-input"
              accept=".zip,application/zip"
              onChange={handleFileChange}
              disabled={isProcessing}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />

            <div className="button-group">
              <button
                type="button"
                className="action-button select-file-button"
                onClick={() => fileInputRef.current.click()}
                disabled={isProcessing}
              >
                <span className="button-icon">📁</span>
                <span className="button-text">Choose ZIP file</span>
              </button>
            </div>

            <div
              ref={dropRef}
              className={`dropzone ${isDragging ? 'dragover' : ''}`}
              onDragOver={onDragOver}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <div className="dropzone-inner">📥 Or drag a ZIP file here</div>
            </div>

            {file && (
              <div className="selected-file-display">
                <span className="file-name">📄 {file.name}</span>
                <span className="file-size">({formatFileSize(file.size)})</span>
                <button
                  type="button"
                  className="btn xs secondary"
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={isProcessing}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="password-input">Password:</label>
          <div className="password-row">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password-input"
              value={password}
              onChange={handlePasswordChange}
              disabled={isProcessing}
              className="password-input"
              placeholder="Enter password"
            />
            <button
              type="button"
              className="inline-btn"
              onClick={() => setShowPassword(s => !s)}
              title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && (
          <div className="success-message">
            {isProcessing ? 'Creating ZIP…' : 'Files extracted successfully!'}
          </div>
        )}

        {renderProgressBar()}

        <div className="form-actions">
          <button
            type="submit"
            disabled={isProcessing || !file || !password}
            className="extract-button"
          >
            {isProcessing ? 'Processing…' : 'Extract'}
          </button>
        </div>
      </form>

      {renderExtractedFiles()}

      <div className="security-note">
        <p>
          <strong>Note:</strong> Extraction happens in your browser. The server has no access to
          your password or unencrypted data.
        </p>
        {fsApiSupported ? (
          <p>
            <strong>Tip:</strong> To extract directly to a local folder, use “Extract to
            directory”. You’ll be asked to choose a folder; the original structure will be
            preserved.
          </p>
        ) : (
          <p>
            <strong>Tip:</strong> To download with the original structure, use “Download as ZIP”.
            This creates a new archive preserving folders.
          </p>
        )}
        {!fsApiSupported && (
          <p>
            <strong>Info:</strong> Your browser doesn’t support the File System Access API to
            extract directly into a folder. Use recent versions of Chrome, Edge or Firefox.
          </p>
        )}
      </div>

      <style>{`
        .local-file-extractor {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background-color: var(--card-bg);
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        h2 {
          margin-top: 0;
          color: var(--text-color);
        }

        .form-group {
          margin-bottom: 20px;
        }

        label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .file-input-container {
          margin-bottom: 15px;
        }

        .button-group {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .action-button {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px 24px;
          border-radius: var(--radius-lg);
          border: 2px solid var(--border-color);
          background: var(--bg-tertiary);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 600;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          background: var(--bg-secondary);
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .action-button:active {
          background: var(--bg-tertiary);
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .action-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-icon {
          margin-right: 8px;
          font-size: 1.2em;
        }

        .selected-file {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          background-color: var(--secondary-bg);
          border-radius: 4px;
          border: 1px solid var(--border-color);
        }

        .file-name {
          margin-right: 8px;
          font-weight: 500;
        }

        .password-input {
          width: 100%;
          padding: 16px 20px;
          border: 2px solid var(--border-color);
          border-radius: var(--radius-lg);
          font-size: 16px;
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .password-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 4px var(--accent-glow);
        }

        .error-message {
          padding: 10px;
          margin-bottom: 15px;
          background-color: var(--danger-light);
          color: var(--danger-color);
          border-radius: 4px;
          border-left: 4px solid var(--danger-color);
        }

        .success-message {
          padding: 10px;
          margin-bottom: 15px;
          background-color: var(--success-light);
          color: var(--success-color);
          border-radius: 4px;
          border-left: 4px solid var(--success-color);
        }

        .progress-container {
          margin: 15px 0;
          background-color: var(--secondary-bg);
          border-radius: 4px;
          height: 20px;
          position: relative;
          overflow: hidden;
        }

        .progress-bar {
          height: 100%;
          background-color: var(--accent-color);
          transition: width 0.3s ease;
        }

        .progress-text {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-primary);
          font-weight: 500;
          font-size: 12px;
        }

        .form-actions {
          margin-top: 20px;
        }

        .extract-button {
          padding: 16px 32px;
          background: var(--accent-gradient);
          color: var(--bg-primary);
          border: none;
          border-radius: var(--radius-lg);
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: var(--shadow-glow-sm);
        }

        .extract-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-glow);
        }

        .extract-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .extracted-files {
          margin-top: 30px;
          padding: 15px;
          background-color: var(--secondary-bg);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .extracted-files h3 {
          margin-top: 0;
          margin-bottom: 15px;
        }

        .file-actions {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          flex-wrap: wrap;
        }
        
        .download-options {
          margin-bottom: 20px;
          background-color: var(--secondary-bg);
          padding: 15px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }
        
        .download-options h4 {
          margin-top: 0;
          margin-bottom: 10px;
          color: var(--text-color);
        }
        
        .download-zip-button {
          background: var(--bg-tertiary);
          border: 2px solid var(--accent-primary);
          color: var(--accent-primary);
          position: relative;
        }

        .download-zip-button:hover {
          background: rgba(0, 212, 255, 0.1);
          border-color: var(--accent-primary);
        }
        
        .primary-action {
          flex: 1;
          font-weight: bold;
          padding: 12px 20px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        
        .button-description {
          font-size: 0.8em;
          opacity: 0.8;
          margin-top: 5px;
          font-weight: normal;
        }

        .file-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .file-item {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          margin-bottom: 8px;
          background-color: var(--card-bg);
          border-radius: 4px;
          border: 1px solid var(--border-color);
        }

        .download-file-button {
          margin-left: auto;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2em;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .download-file-button:hover {
          background-color: var(--accent-light);
        }

        .security-note {
          margin-top: 30px;
          padding: 15px;
          background-color: var(--warning-light);
          border-radius: 4px;
          border-left: 4px solid var(--warning-color);
        }
      `}</style>
    </div>
  );
};

export default LocalFileExtractor;
